import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, CheckCircle, CircleNotch, Scales, WarningCircle } from '@phosphor-icons/react';
import { platformApi, getPlatformApiErrorMessage } from '@/lib/platformApi';

const MIN_LENGTH = 16; // matches platformResetPasswordSchema on the API

/**
 * Sets a new Platform Console password from an emailed link. Also the page an invited admin
 * lands on to choose their first password, which is why it never assumes an existing session.
 */
export default function PlatformResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const navigate = useNavigate();

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [isPending, setIsPending] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [done, setDone] = useState(false);

  const tooShort = password.length > 0 && password.length < MIN_LENGTH;
  const mismatch = confirm.length > 0 && password !== confirm;
  const canSubmit = password.length >= MIN_LENGTH && password === confirm && !isPending;
  const remaining = MIN_LENGTH - password.length;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    setErrorMsg('');
    setIsPending(true);
    try {
      await platformApi.post('/auth/reset-password', { token, newPassword: password });
      setDone(true);
      // Straight to sign-in: the reset revoked every previous session, so there is nothing
      // to return to.
      setTimeout(() => navigate('/platform/login'), 2500);
    } catch (err) {
      setErrorMsg(getPlatformApiErrorMessage(err));
    } finally {
      setIsPending(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-plat-bg px-4">
      <div className="w-full max-w-md">
        <div className="mb-8 flex items-center justify-center gap-2">
          <Scales className="h-7 w-7 text-plat-info-text" weight="fill" />
          <span className="text-lg font-semibold text-plat-fg">Platform Console</span>
        </div>

        <div className="rounded-xl border border-plat-border bg-plat-surface p-6">
          {!token ? (
            <div className="flex items-start gap-3">
              <WarningCircle className="mt-0.5 h-5 w-5 shrink-0 text-plat-warning-text" weight="fill" />
              <div>
                <h2 className="text-[18px] font-semibold text-plat-fg">This link is incomplete</h2>
                <p className="mt-1 text-sm text-plat-secondary">
                  Open the link from your email exactly as it was sent, or request a new one.
                </p>
              </div>
            </div>
          ) : done ? (
            <div className="flex items-start gap-3">
              <CheckCircle className="mt-0.5 h-5 w-5 shrink-0 text-plat-success-text" weight="fill" />
              <div>
                <h2 className="text-[18px] font-semibold text-plat-fg">Password changed</h2>
                <p className="mt-1 text-sm text-plat-secondary">
                  Every existing session was signed out. Taking you to sign in.
                </p>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <h2 className="text-[18px] font-semibold text-plat-fg">Choose a new password</h2>
                <p className="mt-1 text-sm text-plat-secondary">
                  At least {MIN_LENGTH} characters. This account can see every chambers on the
                  platform, so make it long and unique.
                </p>
              </div>

              {errorMsg && (
                <div className="flex items-start gap-2 rounded-lg border border-plat-danger-line bg-plat-danger-soft p-3">
                  <WarningCircle className="mt-0.5 h-4 w-4 shrink-0 text-plat-danger-text" weight="fill" />
                  <p className="text-xs text-plat-danger-text">{errorMsg}</p>
                </div>
              )}

              <div>
                <label htmlFor="password" className="mb-1.5 block text-xs font-medium text-plat-secondary">
                  New password
                </label>
                <input
                  id="password"
                  type="password"
                  required
                  autoFocus
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-11 w-full rounded-lg border border-plat-border bg-plat-bg px-3 text-sm text-plat-fg focus:border-plat-focus focus:outline-none"
                />
                {tooShort && (
                  <p className="mt-1.5 text-xs text-plat-warning-text">
                    {remaining} more {remaining === 1 ? 'character' : 'characters'} needed.
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="confirm" className="mb-1.5 block text-xs font-medium text-plat-secondary">
                  Confirm new password
                </label>
                <input
                  id="confirm"
                  type="password"
                  required
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className="h-11 w-full rounded-lg border border-plat-border bg-plat-bg px-3 text-sm text-plat-fg focus:border-plat-focus focus:outline-none"
                />
                {mismatch && <p className="mt-1.5 text-xs text-plat-warning-text">These do not match.</p>}
              </div>

              <button
                type="submit"
                disabled={!canSubmit}
                className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-lg bg-plat-accent-solid px-5 text-sm font-medium text-plat-on-accent transition hover:bg-plat-accent-solid-hover disabled:cursor-not-allowed disabled:bg-plat-neutral-soft disabled:text-plat-disabled"
              >
                {isPending ? (
                  <>
                    <CircleNotch className="h-4 w-4 animate-spin" />
                    Saving
                  </>
                ) : (
                  'Set new password'
                )}
              </button>
            </form>
          )}

          <div className="mt-6 border-t border-plat-border pt-4">
            <Link
              to="/platform/login"
              className="inline-flex items-center gap-1.5 text-xs text-plat-muted transition hover:text-plat-secondary"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              Back to sign in
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
